import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Textarea,
  Badge,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@h2/ui';
import { X, Plus } from 'lucide-react';
import { useWorkerStore, Worker, WorkerTag, LanguageLevel } from '../../stores/useWorkerStore';

const workerSchema = z.object({
  name: z.string().min(1, '이름을 입력해주세요').max(50, '이름은 50자 이내로 입력해주세요'),
  email: z.string().email('올바른 이메일 주소를 입력해주세요'),
  phone: z.string().optional(),
  hourlyRate: z.number().min(1, '시간당 단가를 입력해주세요'),
  currency: z.string().min(1, '통화를 선택해주세요'),
  status: z.enum(['active', 'inactive']),
  avatar: z.string().optional(),
  memo: z.string().optional(),
});

type WorkerFormData = z.infer<typeof workerSchema>;

interface WorkerFormModalProps {
  worker?: Worker | null;
  onClose: () => void;
}

export const WorkerFormModal: React.FC<WorkerFormModalProps> = ({ worker, onClose }) => {
  const { createWorker, updateWorker } = useWorkerStore();
  const [skills, setSkills] = useState<string[]>(worker?.skills || []);
  const [newSkill, setNewSkill] = useState('');
  const [languageLevels, setLanguageLevels] = useState<LanguageLevel[]>(worker?.languageLevels || []);
  const [newLanguage, setNewLanguage] = useState('');
  const [newLevel, setNewLevel] = useState<'A' | 'B' | 'C'>('A');
  const [tags, setTags] = useState<WorkerTag[]>(worker?.tags || []);
  const [newTagKey, setNewTagKey] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  
  const isEditing = !!worker;

  const form = useForm<WorkerFormData>({
    resolver: zodResolver(workerSchema),
    defaultValues: {
      name: worker?.name || '',
      email: worker?.email || '',
      phone: worker?.phone || '',
      hourlyRate: worker?.hourlyRate || 25000,
      currency: worker?.currency || 'KRW',
      status: worker?.status || 'active',
      avatar: worker?.avatar || '👤',
      memo: worker?.memo || '',
    },
  });

  const onSubmit = async (data: WorkerFormData) => {
    try {
      const workerData = {
        ...data,
        skills: skills,
        languageLevels: languageLevels,
        tags: tags,
      };

      if (isEditing && worker) {
        await updateWorker(worker.id, workerData);
      } else {
        await createWorker(workerData);
      }

      onClose();
    } catch (error) {
      console.error('작업자 저장 실패:', error);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setSkills(skills.filter(skill => skill !== skillToRemove));
  };

  const handleSkillKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  const addLanguageLevel = () => {
    if (newLanguage.trim() && !languageLevels.find(l => l.language === newLanguage.trim())) {
      setLanguageLevels([...languageLevels, { language: newLanguage.trim(), level: newLevel }]);
      setNewLanguage('');
      setNewLevel('A');
    }
  };

  const removeLanguageLevel = (languageToRemove: string) => {
    setLanguageLevels(languageLevels.filter(lang => lang.language !== languageToRemove));
  };

  const addTag = () => {
    if (newTagKey.trim() && newTagValue.trim() && !tags.find(t => t.key === newTagKey.trim())) {
      setTags([...tags, { key: newTagKey.trim(), value: newTagValue.trim() }]);
      setNewTagKey('');
      setNewTagValue('');
    }
  };

  const removeTag = (keyToRemove: string) => {
    setTags(tags.filter(tag => tag.key !== keyToRemove));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? '작업자 수정' : '새 작업자 등록'}</DialogTitle>
          <DialogDescription>
            작업자의 기본 정보와 보유 기술을 입력하세요.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {/* 이름 */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>이름</FormLabel>
                    <FormControl>
                      <Input placeholder="김민수" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 아바타 */}
              <FormField
                control={form.control}
                name="avatar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>아바타</FormLabel>
                    <FormControl>
                      <Input placeholder="👤" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* 이메일 */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>이메일</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="kim@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 전화번호 */}
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>전화번호</FormLabel>
                    <FormControl>
                      <Input placeholder="+82-10-1234-5678" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* 시간당 단가 */}
              <FormField
                control={form.control}
                name="hourlyRate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>시간당 단가</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="25000"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 통화 */}
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>통화</FormLabel>
                    <FormControl>
                      <select {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                        <option value="KRW">KRW (원)</option>
                        <option value="USD">USD (달러)</option>
                        <option value="EUR">EUR (유로)</option>
                        <option value="JPY">JPY (엔)</option>
                        <option value="CNY">CNY (위안)</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 상태 */}
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>상태</FormLabel>
                    <FormControl>
                      <select {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                        <option value="active">활성</option>
                        <option value="inactive">비활성</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 메모 */}
            <FormField
              control={form.control}
              name="memo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>메모</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="작업자에 대한 메모를 입력하세요..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 보유 기술 */}
            <div className="space-y-3">
              <FormLabel>보유 기술</FormLabel>
              <div className="flex gap-2">
                <Input
                  placeholder="기술/언어 추가 (예: 한국어, 영어, 번역)"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyDown={handleSkillKeyDown}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={addSkill}
                  disabled={!newSkill.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {skills.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {skills.map((skill) => (
                    <Badge key={skill} variant="secondary" className="text-sm">
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeSkill(skill)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* 언어 등급 */}
            <div className="space-y-3">
              <FormLabel>언어 등급</FormLabel>
              <div className="flex gap-2">
                <Input
                  placeholder="언어 코드 (예: KO, EN, JA)"
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  className="flex-1"
                />
                <select 
                  value={newLevel} 
                  onChange={(e) => setNewLevel(e.target.value as 'A' | 'B' | 'C')}
                  className="flex h-10 w-20 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
                <Button
                  type="button"
                  onClick={addLanguageLevel}
                  disabled={!newLanguage.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {languageLevels.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {languageLevels.map((lang) => (
                    <Badge key={lang.language} variant="outline" className="text-sm">
                      {lang.language}: {lang.level}
                      <button
                        type="button"
                        onClick={() => removeLanguageLevel(lang.language)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* 태그 */}
            <div className="space-y-3">
              <FormLabel>태그</FormLabel>
              <div className="flex gap-2">
                <Input
                  placeholder="키 (예: 전문분야)"
                  value={newTagKey}
                  onChange={(e) => setNewTagKey(e.target.value)}
                  className="flex-1"
                />
                <Input
                  placeholder="값 (예: 기술번역)"
                  value={newTagValue}
                  onChange={(e) => setNewTagValue(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={addTag}
                  disabled={!newTagKey.trim() || !newTagValue.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {tags.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {tags.map((tag) => (
                    <Badge key={tag.key} variant="secondary" className="text-sm">
                      {tag.key}: {tag.value}
                      <button
                        type="button"
                        onClick={() => removeTag(tag.key)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* 버튼 */}
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={onClose}>
                취소
              </Button>
              <Button type="submit">
                {isEditing ? '수정' : '등록'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
